import React from 'react';
import {connect} from 'react-redux';
import {defineMessages, FormattedMessage, FormattedNumber} from 'react-intl';

import {cssClassMap} from 'rendering-commons/SportCssMap';

import {removeCouponRate as removeCouponRateAction} from 'actions/creators/CouponRate';
import {requestBetTypes as requestBetTypesAction} from 'actions/creators/HistoryBet';
import {toggleFreebet as toggleFreebetAction} from 'actions/creators/Freebet';
import {changeCouponSlipAmount as changeCouponSlipAmountAction} from 'actions/creators/CouponRate';
import {isRateUnavailable, rateFactorChangeConfirmNeeded} from './rateUtils';

import {minAmount as MinAmountValues} from 'constants/StakeMinAmount';
import {maxWinAmount as MaxWinAmountValues} from 'constants/StakeMaxWinAmount';
import {resourceNames} from 'constants/accountResourceNames';

const defaultResourceId = 4;

const messages = defineMessages({
    rateChanged: {
        id: 'coupon.rate_changed',
        defaultMessage: 'Коэфф недоступен'
    },
    rateUnavailable: {
        id: 'coupon.rate_unavailable',
        defaultMessage: 'Прием закрыт'
    },
    confirmRateValueChange: {
        id: 'coupon.confirm_rate_value_change',
        defaultMessage: 'Принять изменения'
    },
    stake: {
        id: 'coupon.stake_title',
        defaultMessage: 'Ставка:'
    },
    rate: {
        id: 'coupon.rate_title',
        defaultMessage: 'Коэфф:'
    },
    sumLabel: {
        id: 'coupon.sum_label',
        defaultMessage: 'Сумма'
    },
    minAmountHint: {
        id: 'coupon.min_amount_hint',
        defaultMessage: 'Мин.'
    },
    possible_winning_hint: {
        id: 'coupon.possible_winning_hint_short',
        defaultMessage: 'Выигрыш'
    },
    maxAmountHint: {
        id: 'coupon.max_amount_hint',
        defaultMessage: 'Макс. {amount} {resource}'
    }
});

class BetSlipExt extends React.Component {
    static propTypes = {
        user: React.PropTypes.any,
        rate: React.PropTypes.object,
        match: React.PropTypes.object,
        score: React.PropTypes.object,
        outcome: React.PropTypes.object,
        bettingMode: React.PropTypes.object,
        amount: React.PropTypes.number,
        minBetAmount: React.PropTypes.number,
        removeCouponRateAction: React.PropTypes.func.isRequired,
        requestBetTypesAction: React.PropTypes.func.isRequired,
        changeCouponSlipAmountAction: React.PropTypes.func.isRequired,
        toggleFreebetAction: React.PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);

        const rawAmount = (this.props.amount > 0) ? Number(this.props.amount) : 0;

        this.state = {rawAmount};
        this._onAmountChange = this._onAmountChange.bind(this);
        this._onToggleFreebetClick = this._onToggleFreebetClick.bind(this);
        this._onRemoveClick = this._onRemoveClick.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        const rawAmount = Number(this.state.rawAmount);

        if (rawAmount !== nextProps.amount) {
            this.setState({rawAmount: nextProps.amount});
        }
    }

    _onAmountChange(e) {
        e.preventDefault();

        const {rate} = this.props;
        const rateId = rate.get('id');
        let rawAmount = e.target.value;

        // checking for length
        if (!rawAmount.length) {
            this.setState({rawAmount: ''});
            this.props.changeCouponSlipAmountAction([{rateId, amount: 0}]);
        }

        const amount = Number(rawAmount);

        if (isNaN(amount)) {
            return;
        }

        const splittedRawAmount = rawAmount.split('.');
        const zeroSum = (splittedRawAmount[0].length > 1 && amount == 0.0);
        const longDecimalPart = (splittedRawAmount[1] !== undefined && splittedRawAmount[1].length > 2);

        // checking for several zeros and long decimal part of value
        if (zeroSum || longDecimalPart) {
            return;
        }

        // concating splitted value to normal amount
        rawAmount = Number(splittedRawAmount[0]).toString();
        if (splittedRawAmount[1] !== undefined) {
            rawAmount = rawAmount + '.' + splittedRawAmount[1];
        }

        const splittedAmount = amount.toString().split('.');
        const ceilPart = splittedAmount[0];
        const decimalPart = splittedAmount[1] !== undefined ? splittedAmount[1] : '';

        // checking for fract.num length
        if ((ceilPart.toString().length > 7) || (decimalPart.toString().length > 2)) {
            return;
        }

        this.setState({rawAmount});
        this.props.changeCouponSlipAmountAction([{rateId, amount: amount}]);
    }

    _onRemoveClick(e) {
        e.preventDefault();
        Promise.resolve(
            this.props.removeCouponRateAction({rateId: this.props.rate.get('id')})
        ).then(
            () => this.props.onUpdateSize()
        );
        this.props.requestBetTypesAction();
    }

    _onToggleFreebetClick(e) {
        e.preventDefault();

        const {freebet, isFreebet, rate} = this.props;
        const rateId = rate.get('id');

        if (isFreebet) {
            this.setState({rawAmount: 0});
            this.props.toggleFreebetAction(false);
            this.props.changeCouponSlipAmountAction([{rateId, amount: 0, isFreeBet: false}]);
        } else {
            const freebetAmount = freebet.get('amount');

            this.setState({rawAmount: freebetAmount});
            this.props.toggleFreebetAction(true);
            this.props.changeCouponSlipAmountAction([{rateId, amount: freebetAmount, isFreeBet: true}]);
        }
    }

    render() {
        const {
            rate,
            match,
            score,
            outcome,
            bettingMode,
            stake,
            amount,
            alternativeStake,
            minBetAmount,
            maxWinAmount,
            resourceName,
            isFreebet,
            freebetAvailable
        } = this.props;

        const {rawAmount} = this.state;
        const ltMinAmount = (amount < minBetAmount);
        const originalValue = rate.get('value');
        const actualValue = rate.get('actualValue');
        const acceptFactorChange = bettingMode.get('acceptFactorChange');
        const sportIconCls = 'sport-icon svg-' + cssClassMap[rate.get('sportId')];
        const possibleWinning = ltMinAmount ? 0 : parseFloat(amount * originalValue).toFixed(2);

        const maxRestriction = possibleWinning > maxWinAmount;

        let unavailableSpan;
        if (isRateUnavailable(rate)) {
            unavailableSpan = (
                <FormattedMessage {...messages.rateUnavailable}>
                    {(message) => <span className="event-rate event-rate__unavailable">{message}</span>}
                </FormattedMessage>
            );
        } else if (rateFactorChangeConfirmNeeded(rate, acceptFactorChange)) {
            unavailableSpan = (
                <FormattedMessage {...messages.rateChanged}>
                    {(message) => <span className="event-rate event-rate__unavailable">{message}</span>}
                </FormattedMessage>
            );
        }

        let scoreSpan = undefined;
        if (score) {
            let score1 = score.get('score1', 0);
            let score2 = score.get('score2', 0);

            scoreSpan = (
                <span className="current-score">
                    <strong>{score1 + ':' + score2}</strong>
                </span>
            );
        }

        let rateSpan = (
            <span className="event-rate">
                <FormattedMessage {...messages.rate} />
                <strong>{originalValue}</strong>
                <span className="event-single-bet-amount">
                    <FormattedMessage{...messages.sumLabel} />:
                    <strong>
                        <FormattedNumber value={stake}/>
                    </strong>
                </span>
                {unavailableSpan}
            </span>
        );

        const rateChanged = (actualValue && originalValue !== actualValue);

        if (rateChanged) {
            let cls = (originalValue < actualValue) ? 'new-rate up' : 'new-rate down';

            rateSpan = (
                <div className="rate changed">
                    <FormattedMessage {...messages.rate} />
                    <span className="old-rate"> {originalValue}</span>
                    {'\u00A0'}
                    <span className={cls}>{actualValue}</span>
                    {unavailableSpan}
                </div>
            );
        }

        let matchName = match.get('name');
        if (match.get('notice')) {
            matchName += '(' + match.get('notice') + ')'
        }

        return (
            <li className="coupons-list-item">
                <span className="event-name">
                    <span className={sportIconCls}/>
                    {scoreSpan}
                    {matchName}<br/>
                    {outcome.get('name')}
                </span>
                <span className="event-type">
                    <FormattedMessage {...messages.stake} />
                    <strong>{rate.get('shortName')}</strong>
                </span>
                {rateSpan}
                <div className="singlebet-amount cf">
                    <input value={rawAmount} type="text" readOnly={isFreebet} id="bet-amount" className="bet"
                           onChange={this._onAmountChange} onBlur={this._onAmountChange}/>
                    {ltMinAmount &&
                    <p className="min-bet-amount-hint">
                        <FormattedMessage {...messages.minAmountHint} /> {minBetAmount}
                    </p>}
                    {freebetAvailable && <div className="freebet--icon freebet--icon__active freebet--icon__bet-amount"
                                              onClick={this._onToggleFreebetClick}/>}
                </div>
                <span className="singlebet-amount">
                    <FormattedMessage {...messages.possible_winning_hint} />:
                    <strong>
                        <FormattedNumber value={possibleWinning}/>
                    </strong>
                </span>
                {maxRestriction && <FormattedMessage {...messages.maxAmountHint }
                                                     values={{amount: maxWinAmount, resource: resourceName}}>
                    {(message) => <p className="max-win-amount-hint-ext">{message}</p>}
                </FormattedMessage>}
                {alternativeStake && <FormattedMessage {...messages.maxAmountHint }
                                                     values={{amount: alternativeStake, resource: resourceName}}>
                    {(message) => <p className="max-win-amount-hint-ext">{message}</p>}
                </FormattedMessage>}
                <a href="#" className="close" onClick={this._onRemoveClick}>{'\u00A0'}</a>
            </li>
        );
    }
}

function isFreeBetAvailable(oddValue, maxFactorValue) {
    const odd = Number(oddValue);
    const maxFactor = Number(maxFactorValue);

    return (!maxFactor || maxFactor == 0.0 || odd <= maxFactor);
}

function select(state, props) {
    const user = state.auth.get('user');
    const bettingMode = state.application.get('bettingMode');
    const resourceName = (user === undefined || !user)
        ? resourceNames[defaultResourceId]
        : resourceNames[user.get('resourceId')];
    const minBetAmount = MinAmountValues[resourceName];
    const maxWinAmount = MaxWinAmountValues[resourceName];

    const betSlipId = props.id;
    const betSlip = state.application.getIn(['coupon', 'betSlips', betSlipId]);

    const rate = betSlip.get('rate');
    const match = betSlip.get('match');
    const score = betSlip.get('score');
    const outcome = betSlip.get('outcome');

    const rateId = rate.get('id');
    const betSlipAmount = state.application.getIn(['coupon', 'betSlipAmounts', rateId]);

    const stake = betSlipAmount.get('amount');
    const amount = betSlipAmount.get('amount');
    const alternativeStake = betSlipAmount.get('alternativeStake');

    const freebet = state.application.getIn(['coupon', 'freebet']);
    const isFreebet = state.application.getIn(['coupon', 'isFreebet', 'isFreebet']);

    const freebetAvailable = (!freebet || freebet.size === 0)
        ? false
        : isFreeBetAvailable(rate.get('value'), freebet.get('maxFactorValue'));

    const isFreebetSlip = betSlipAmount.get('isFreeBet');

    const freebetEnable = ((freebetAvailable && !isFreebet) || (freebetAvailable && isFreebet && isFreebetSlip));

    return {
        rate,
        match,
        score,
        outcome,
        bettingMode,
        stake,
        amount,
        alternativeStake,
        freebet,
        isFreebet: isFreebetSlip,
        freebetAvailable: freebetEnable,
        minBetAmount,
        maxWinAmount,
        resourceName,
        user
    };
}

export default connect(select, {
    removeCouponRateAction,
    requestBetTypesAction,
    changeCouponSlipAmountAction,
    toggleFreebetAction
})(BetSlipExt);



// WEBPACK FOOTER //
// ./js/sport-betting/components/bet-panel-control/coupon/BetSlipeExt.jsx